import { useRef, useState } from "react";
import { useI18n } from "../../i18n/index.js";
import type { CrowflightPlace } from "./api.js";
import styles from "./SortRevealList.module.css";

export interface SortRevealEntry {
  place: CrowflightPlace;
  isGreen: boolean;
  truePosition: number;
  points: number;
  valueLabel: string;
}

interface SortRevealListProps {
  entries: SortRevealEntry[];
  correctOrder: CrowflightPlace[];
  showCorrectOrderLabel: string;
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Same FLIP-animated reveal pattern as Timeslip's RevealList (spec §6.2), reused for Crowflight R1/R2. */
export function SortRevealList({ entries, correctOrder, showCorrectOrderLabel }: SortRevealListProps) {
  const { lang } = useI18n();
  const [order, setOrder] = useState<SortRevealEntry[]>(entries);
  const [animating, setAnimating] = useState(false);
  const nodeRefs = useRef(new Map<number, HTMLLIElement>());

  function setNodeRef(id: number, el: HTMLLIElement | null) {
    if (el) nodeRefs.current.set(id, el);
    else nodeRefs.current.delete(id);
  }

  function flipTo(nextOrder: SortRevealEntry[]) {
    const before = new Map<number, number>();
    for (const [id, el] of nodeRefs.current) before.set(id, el.getBoundingClientRect().top);

    setOrder(nextOrder);

    requestAnimationFrame(() => {
      const reduced = prefersReducedMotion();
      for (const [id, el] of nodeRefs.current) {
        const prevTop = before.get(id);
        if (prevTop === undefined) continue;
        const newTop = el.getBoundingClientRect().top;
        const delta = prevTop - newTop;
        if (delta === 0) continue;
        el.style.transition = "none";
        el.style.transform = `translateY(${delta}px)`;
        void el.offsetHeight;
        el.style.transition = reduced ? "none" : "transform 0.4s ease";
        el.style.transform = "translateY(0)";
      }
    });
  }

  function handleShowCorrectOrder() {
    if (animating) return;
    setAnimating(true);
    const correctEntryOrder = correctOrder.map(
      (place) => order.find((e) => e.place.id === place.id) ?? entries.find((e) => e.place.id === place.id)!,
    );
    flipTo(correctEntryOrder);
    const holdMs = prefersReducedMotion() ? 500 : 1400;
    window.setTimeout(() => {
      flipTo(entries);
      setAnimating(false);
    }, holdMs);
  }

  return (
    <div>
      <ol className={styles.list}>
        {order.map((entry) => (
          <li
            key={entry.place.id}
            ref={(el) => setNodeRef(entry.place.id, el)}
            className={`${styles.card} ${entry.isGreen ? styles.correct : styles.incorrect}`}
          >
            <div className={styles.row}>
              <span className={styles.icon} aria-hidden>
                {entry.isGreen ? "✓" : "✗"}
              </span>
              <span className={styles.text}>{lang === "de" ? entry.place.nameDe : entry.place.nameEn}</span>
              <span className={styles.value}>{entry.valueLabel}</span>
            </div>
            {entry.isGreen && <div className={styles.pointsBadge}>+{entry.points}</div>}
          </li>
        ))}
      </ol>
      <button type="button" className={styles.showOrderButton} onClick={handleShowCorrectOrder} disabled={animating}>
        {showCorrectOrderLabel}
      </button>
    </div>
  );
}
